import awsAuthErrorTypes from '../consts/aws/awsAuthErrorTypes';
import awsRumActionTypes from '../consts/aws/awsRumActionTypes';
import notificationActionTypes from '../consts/notification/notificationActionTypes';
import notificationTypes from '../consts/notification/notificationTypes';
import pushNotificationDestinationTypes from '../consts/notification/pushNotificationDestinationTypes';
import routes from '../consts/routes';
import {strCode} from '../consts/strCode';
import * as uamConnectionStatusTypes from '../consts/uam/uamConnectionStatusTypes';
import {selectIsPWA} from '../state/selectors/global';
import {
    selectCurrentDusn,
    selectCurrentHolderDusn,
    selectDeviceConnectionType,
    selectDeviceName,
    selectIotDevicesList,
    selectIsDeviceRegisteredByDusn,
    selectIsHolderType,
} from '../state/selectors/iotDevices';
import {setNotification} from '../state/slice/global';
import {dispatch, getState} from '../state/store';
import ecPushObserver from './events/ecPushObserver';
import rumCustomEvents from './events/rumCustomEvents';
import {formatString, t} from './localization/localizationService';

const showNotification = (data) => {
    const {dusn} = data || {};

    const currentDusn = selectCurrentDusn(getState());
    const currentHolderDusn = selectCurrentHolderDusn(getState());

    // Show message only for current selected device or current holder
    if (!!dusn && dusn !== currentDusn && dusn !== currentHolderDusn) return;

    if (dusn) {
        const isDeviceRegistered = selectIsDeviceRegisteredByDusn(dusn)(getState());

        // Prevents notifications from being displayed after logout or unregistering the device
        if (!isDeviceRegistered) return;
    }

    dispatch(setNotification(data));
};

const showPushNotification = (data) => {
    const {
        dusn,
        modalType,
        destinationType,
        notificationType,
        notificationSubType,
        titleText,
        messageText,
        id,
        timestamp,
    } = data || {};

    ecPushObserver.observe({messageId: id, notificationSubType});

    rumCustomEvents.pushPushNotification({
        dusn,
        modalType,
        destinationType,
        notificationType,
        notificationSubType,
        titleText,
        messageText,
        eventId: id,
        action: awsRumActionTypes.RECEIVED,
    });

    showNotification({
        type: notificationTypes.PUSH_NOTIFICATION,
        id,
        timestamp: timestamp || Date.now(),
        ...data,
    });
};

const showToastNotification = (data) =>
    showNotification({
        actionStr: strCode.GLOBAL_DISMISS_BUTTON,
        actionType: notificationActionTypes.CLOSE,
        type: notificationTypes.TOAST_NOTIFICATION,
        ...data,
    });

// Custom notifications
const showRequestError = (error) => {
    const isUserNotAuthorizedError = error?.code === awsAuthErrorTypes.NOT_AUTHORIZED_EXCEPTION;

    if (isUserNotAuthorizedError) return;

    showToastNotification({messageStr: strCode.REQUEST_ERROR});
};

const showPostBadgesError = (isOfflineError) => {
    const currentPage = window?.location?.pathname;

    const DISABLED_PAGES = [
        routes.LTE_CONNECTION_GUIDE_INTRO,
        routes.WIFI_CONNECTION_GUIDE_INTRO,
        routes.LTE_CONNECTION_GUIDE_STEPS,
        routes.WIFI_CONNECTION_GUIDE_STEPS,
    ];

    if (DISABLED_PAGES.includes(currentPage)) {
        return;
    }

    showToastNotification({
        messageStr: isOfflineError ? strCode.CLUB_PAGE_POST_BADGE_OFFLINE_MESSAGE : strCode.CLUB_PAGE_POST_BADGE_ERROR,
    });
};

const showComingSoonWarning = () => showToastNotification({messageStr: strCode.GLOBAL_COMING_SOON_WARN});

const showLocalizationError = () => showToastNotification({messageStr: strCode.LOCALIZATION_ERROR});

const showNetworkWarning = () => showToastNotification({messageStr: strCode.CHECK_NETWORK_CONNECTION_WARN});

const showRequestSavedSuccess = () => showToastNotification({messageStr: strCode.REQUEST_SAVED_SUCCESS_MESSAGE});

const showSessionExpiredError = () => showToastNotification({messageStr: strCode.LOGIN_PAGE_ERROR_SESSION_EXPIRED});

const showInactiveSessionError = () => showToastNotification({messageStr: strCode.INACTIVE_SESSION_ERROR});

const showSessionAccessError = () => showToastNotification({messageStr: strCode.LOGIN_PAGE_ERROR_SESSION_ACCESS});

const showUpdatePwaWarning = () => {
    const isPWA = selectIsPWA(getState());

    showToastNotification({
        titleStr: isPWA ? strCode.APP_UPDATE_AVAILABLE_PWA_TITLE : strCode.APP_UPDATE_AVAILABLE_BROWSER_TITLE,
        messageStr: isPWA ? strCode.APP_UPDATE_AVAILABLE_PWA_MESSAGE : strCode.APP_UPDATE_AVAILABLE_BROWSER_MESSAGE,
        actionStr: isPWA ? strCode.APP_UPDATE_AVAILABLE_PWA_BUTTON : strCode.APP_UPDATE_AVAILABLE_BROWSER_BUTTON,
        actionType: notificationActionTypes.RELOAD_APP,
        id: 'pwa-update-notification',
        isExpired: false,
    });
};

const showReconnectDeviceWarning = (dusn) => {
    const deviceName = selectDeviceName(dusn)(getState());
    const isHolderType = selectIsHolderType(dusn)(getState());
    const isConnectionTypeAvailable = selectDeviceConnectionType(dusn)(getState());

    showToastNotification({
        titleText: formatString(strCode.RECONNECT_DEVICE_TITLE, deviceName),
        messageStr: isHolderType ? strCode.RECONNECT_HOLDER_MESSAGE : strCode.RECONNECT_DEVICE_MESSAGE,
        actionStr: strCode.RECONNECT_DEVICE_BUTTON,
        actionType: isConnectionTypeAvailable
            ? notificationActionTypes.SHOW_RECONNECT_GUIDE
            : notificationActionTypes.RECONNECT_DEVICE,
        dusn,
    });
};

const showReconnectDeviceKitWarning = (dusn) => {
    const deviceName = selectDeviceName(dusn)(getState());
    const isConnectionTypeAvailable = selectDeviceConnectionType(dusn)(getState());

    showToastNotification({
        titleText: formatString(strCode.RECONNECT_DEVICE_TITLE, deviceName),
        messageStr: strCode.RECONNECT_DEVICE_MESSAGE,
        actionStr: strCode.RECONNECT_DEVICE_BUTTON,
        actionType: isConnectionTypeAvailable
            ? notificationActionTypes.SHOW_RECONNECT_GUIDE
            : notificationActionTypes.RECONNECT_DEVICE_KIT,
        dusn,
    });
};

const showRefreshDeviceSettingsWarning = (dusn) => {
    const deviceName = selectDeviceName(dusn)(getState());

    showToastNotification({
        titleText: formatString(strCode.REFRESH_DEVICE_SETTING_TITLE, deviceName),
        messageStr: strCode.REFRESH_DEVICE_DEVICE_MESSAGE,
        actionStr: strCode.REFRESH_DEVICE_DEVICE_BUTTON,
        actionType: notificationActionTypes.RECONNECT_DEVICE,
        dusn,
    });
};

const showRefreshDeviceKitSettingsWarning = (dusn) => {
    const deviceName = selectDeviceName(dusn)(getState());

    showToastNotification({
        titleText: formatString(strCode.REFRESH_DEVICE_SETTING_TITLE, deviceName),
        messageStr: strCode.REFRESH_DEVICE_DEVICE_MESSAGE,
        actionStr: strCode.REFRESH_DEVICE_DEVICE_BUTTON,
        actionType: notificationActionTypes.RECONNECT_DEVICE_KIT,
        dusn,
    });
};

const showProcessInProgressWarning = () =>
    showToastNotification({messageStr: strCode.GLOBAL_PROCESS_IS_RUNNING_WARNING});

const showActiveRequestAlreadyExistsWarning = (dusn) =>
    showToastNotification({messageStr: strCode.DEVICE_SETTINGS_PAGE_ACTIVE_REQUEST_ALREADY_EXISTS_WARNING, dusn});

const showDeviceConnectInProgressWarning = () =>
    showToastNotification({messageStr: strCode.GLOBAL_DEVICE_CONNECT_IS_RUNNING_WARNING});

const showDeviceSyncInProgressWarning = () =>
    showToastNotification({
        titleStr: strCode.GLOBAL_DEVICE_SYNC_IS_RUNNING_WARNING_TITLE,
        messageStr: strCode.GLOBAL_DEVICE_SYNC_IS_RUNNING_WARNING_DESCRIPTION,
    });

// TEMPORARY: mocked FWU flow
const showPushNotificationAboutNewFwVersion = () => {
    let currentDusn = selectCurrentDusn(getState());

    // If current dusn is not selected, get the first CONNECTED from store
    if (!currentDusn) {
        const iotDevicesList = selectIotDevicesList(getState());

        const sortedDevices = Object.values(iotDevicesList)
            .map(({data}) => ({dusn: data.dusn, status: data.device.connectionStatus}))
            .sort((a) => (a.status === uamConnectionStatusTypes.CONNECTED ? -1 : 1));

        if (sortedDevices.length > 0) {
            currentDusn = sortedDevices[0]?.serialNumber;
        } else {
            return;
        }
    }

    showPushNotification({
        messageText: t(strCode.FWU_NEW_VERSION_AVAILABLE_NOTIFICATION),
        destinationType: pushNotificationDestinationTypes.SOFTWARE_UPDATE,
        dusn: currentDusn,
        isMock: true,
    });
};

const showDeviceIsNotRegisteredWarning = () =>
    showToastNotification({messageStr: strCode.MESSAGE_CENTER_PAGE_DEVICE_IS_NOT_REGISTERED_NOTIFICATION});

const showDeviceLockWarning = () =>
    showToastNotification({
        titleStr: strCode.HOME_PAGE_SETTINGS_DEVICE_IS_LOCKED_MESSAGE_TITLE,
        messageStr: strCode.HOME_PAGE_SETTINGS_DEVICE_IS_LOCKED_MESSAGE_DESCRIPTION,
        actionStr: strCode.HOME_PAGE_SETTINGS_DEVICE_IS_LOCKED_MESSAGE_BUTTON,
        actionType: notificationActionTypes.DEVICE_LOCK,
    });

const showExperienceInferenceCouldNotReceiveData = () =>
    showToastNotification({
        messageStr: strCode.EXPERIENCE_INFERENCE_COULD_NOT_RECEIVE_DATA_MESSAGE,
    });

const showExperienceScoreUnavailableMessage = () =>
    showToastNotification({
        messageStr: strCode.EXPERIENCE_SCORE_PAGE_UNAVAILABLE_MESSAGE,
    });

const showLocationPermissionDenied = () =>
    showToastNotification({
        titleStr: strCode.USER_LOCATION_PERMISSION_DENIED_NOTIFICATION_TITLE,
        messageStr: strCode.USER_LOCATION_PERMISSION_DENIED_NOTIFICATION_MESSAGE,
    });

const showUserLocationUnavailable = () =>
    showToastNotification({
        titleStr: strCode.USER_LOCATION_UNAVAILABLE_NOTIFICATION_TITLE,
        messageStr: strCode.USER_LOCATION_UNAVAILABLE_NOTIFICATION_MESSAGE,
    });

const showPushSubscriptionError = () => {
    showToastNotification({messageStr: strCode.PUSH_SUBSCRIPTION_ERROR});
};

const showGettingRewardListError = () => {
    showToastNotification({messageStr: strCode.GETTING_REWARD_LIST_ERROR});
};

const showOutdatedDataMessage = () => {
    showToastNotification({messageStr: strCode.CLUB_PAGE_OUTDATED_DATA_MESSAGE});
};

const showRemoveNetworkNotification = (ssid) =>
    showToastNotification({
        messageText: formatString(strCode.NETWORK_UNDO_REMOVE_MESSAGE, ssid),
        actionStr: strCode.NETWORK_UNDO_REMOVE_BUTTON,
        actionType: notificationActionTypes.UNDO_REMOVE_NETWORK,
        params: {ssid},
    });

const showUndoRemoveNotification = (params) =>
    showToastNotification({
        messageStr: strCode.MESSAGE_CENTER_PAGE_UNDO_REMOVE_TITLE,
        actionStr: strCode.MESSAGE_CENTER_PAGE_UNDO_REMOVE_BTN,
        actionType: notificationActionTypes.UNDO_REMOVE_NOTIFICATION,
        params,
    });

const showSecondWifiRegistrationSuccess = (dusn) => {
    const deviceName = selectDeviceName(dusn)(getState());

    showToastNotification({
        titleStr: strCode.SECOND_WIFI_DEVICE_REGISTRATION_SUCCESS_MESSAGE_TITLE,
        messageText: formatString(strCode.SECOND_WIFI_DEVICE_REGISTRATION_SUCCESS_MESSAGE_DESCRIPTION, deviceName),
    });
};

const showDeviceRegistrationSuccessNotification = () => {
    showToastNotification({messageStr: strCode.DEVICE_ALREADY_REGISTERED_SUCCESS_MESSAGE});
};

const showFeedbackSubmittedSuccess = () =>
    showToastNotification({messageStr: strCode.NOTIFICATIONS_FEEDBACK_MODAL_SUCCESS_MESSAGE});

export default {
    showToastNotification,
    showPushNotification,
    showRequestError,
    showComingSoonWarning,
    showNetworkWarning,
    showRequestSavedSuccess,
    showUpdatePwaWarning,
    showReconnectDeviceWarning,
    showReconnectDeviceKitWarning,
    showRefreshDeviceSettingsWarning,
    showRefreshDeviceKitSettingsWarning,
    showProcessInProgressWarning,
    showActiveRequestAlreadyExistsWarning,
    showDeviceConnectInProgressWarning,
    showDeviceSyncInProgressWarning,
    showSessionExpiredError,
    showSessionAccessError,
    showLocalizationError,
    showPushNotificationAboutNewFwVersion,
    showDeviceIsNotRegisteredWarning,
    showDeviceLockWarning,
    showExperienceInferenceCouldNotReceiveData,
    showExperienceScoreUnavailableMessage,
    showLocationPermissionDenied,
    showUserLocationUnavailable,
    showPushSubscriptionError,
    showInactiveSessionError,
    showGettingRewardListError,
    showOutdatedDataMessage,
    showPostBadgesError,
    showRemoveNetworkNotification,
    showUndoRemoveNotification,
    showSecondWifiRegistrationSuccess,
    showDeviceRegistrationSuccessNotification,
    showFeedbackSubmittedSuccess,
};
